import React, { useEffect, useRef, useState } from 'react';
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';
import axios from 'axios';
import { Bot, UserRound } from 'lucide-react';
import { tmAIAssistantDefaultValue } from '../constant';
import { forceLogout, shouldLogout } from '../utils/sessionManager';
import Navbar from '../components/navbar';
import { HiMicrophone, HiStop } from 'react-icons/hi';
import { useTheme } from '../context/ThemeContext';

/* -------------------- CONSTANTS -------------------- */

// Allow natural speaking pauses so users can think/speak comfortably.
const USER_RESPONSE_DEBOUNCE_MS = 5000;
const PAUSE_REMINDER_COOLDOWN_MS = 12000;
const WAITING_PULSE_DELAY_MS = 1200;
const WAITING_PULSE_INTERVAL_MS = 1200;

/* -------------------- SPEECH STATE (GLOBAL) -------------------- */
let currentUtterance = null;
let currentText = '';
let currentCharIndex = 0;
let stopWaitingPulseExternal = () => {};
let startFallbackTimer = null;

const isAudioMuted = () => {
  try {
    return localStorage.getItem('rnibMuted') === 'true';
  } catch {
    return false;
  }
};

const selectFemaleVoice = (voiceList) => {
  // Comprehensive list of female voice names across all browsers
  const allFemaleVoiceNames = [
    // Premium UK female voices
    'victoria',
    'karen',
    'moira',
    'samantha',
    'fiona',
    'susan',
    // Alternative UK female voices
    'zoe',
    'serena',
    'hazel',
    'lucy',
    'anna',
    'ellen',
    'emily',
    // US female voices
    'zira',
    'cortana',
    'julia',
    'aria',
    // Google/Chrome female voices
    'google uk english female',
    'google us english female',
    // Additional known female names
    'siri',
    'alexa',
    'female',
    'woman',
    'lady',
    'girl',
    'ava',
    'bella',
    'clara',
    'diana',
    'eva',
    'grace',
    'ivy',
    'kate',
    'lily',
    'mia',
    'olivia',
    'rose',
    'sophia',
    'lily',
  ];

  // Priority 1: Find premium UK English voices (including Google)
  const ukPremiumFemaleNames = ['Google UK English Female'];
  for (const name of ukPremiumFemaleNames) {
    const voice = voiceList.find((v) => v.lang === 'en-GB' && v.name.toLowerCase() === name);
    if (voice) return voice;
  }

  // Priority 2: Find any UK female voice (en-GB)
  const ukVoices = voiceList.filter((v) => v.lang === 'en-GB');
  for (const voice of ukVoices) {
    const nameLower = voice.name.toLowerCase();
    if (allFemaleVoiceNames.some((name) => nameLower.includes(name))) {
      return voice;
    }
  }

  // Priority 3: Find US female voices (en-US)
  const usVoices = voiceList.filter((v) => v.lang.startsWith('en-US'));
  for (const voice of usVoices) {
    const nameLower = voice.name.toLowerCase();
    if (allFemaleVoiceNames.some((name) => nameLower.includes(name))) {
      return voice;
    }
  }

  // Priority 4: Find any English female voice
  const englishVoices = voiceList.filter((v) => v.lang.startsWith('en'));
  for (const voice of englishVoices) {
    const nameLower = voice.name.toLowerCase();
    if (allFemaleVoiceNames.some((name) => nameLower.includes(name))) {
      return voice;
    }
  }

  // Priority 5: If no explicit female match found, try to find voice with 'female' keyword
  const genericFemaleVoice = voiceList.find(
    (v) => v.name.toLowerCase().includes('female') && v.lang.startsWith('en')
  );
  if (genericFemaleVoice) return genericFemaleVoice;

  // Priority 6: Return first non-male English voice (fallback strategy)
  const maleKeywords = [
    'male',
    'man',
    'boy',
    'george',
    'david',
    'john',
    'james',
    'robert',
    'michael',
    'daniel',
  ];
  const nonMaleVoice = voiceList.find(
    (v) =>
      v.lang.startsWith('en') &&
      !maleKeywords.some((keyword) => v.name.toLowerCase().includes(keyword))
  );
  if (nonMaleVoice) return nonMaleVoice;

  // Final fallback: return any English voice (when no voice data is available)
  // This ensures the app works even in edge cases
  return voiceList.find((v) => v.lang.startsWith('en'));
};

/* -------------------- TTS WITH RESUME + CONVERSATIONAL UK VOICE -------------------- */
const speak = (text, playbackRate = 1, onEnd) => {
  const synth = window.speechSynthesis;
  if (!synth || !text) return;
  if (isAudioMuted()) return;

  SpeechRecognition.stopListening();

  if (text !== currentText) {
    synth.cancel();
    currentText = text;
    currentCharIndex = 0;
  }

  const remainingText = currentText.slice(currentCharIndex);
  if (!remainingText) return;

  const setVoiceAndSpeak = () => {
    const voices = synth.getVoices();
    const selectedVoice = selectFemaleVoice(voices);

    currentUtterance = new SpeechSynthesisUtterance(remainingText);

    if (selectedVoice) {
      currentUtterance.voice = selectedVoice;
      currentUtterance.lang = selectedVoice.lang;
    } else {
      currentUtterance.lang = 'en-GB';
    }

    // Set voice properties for natural, conversational UK female tone
    // Slightly slower for clarity, natural pitch for warmth, good volume
    currentUtterance.rate = playbackRate * 0.82;
    currentUtterance.pitch = 1.08;
    currentUtterance.volume = 0.88;

    currentUtterance.onboundary = (event) => {
      if (event.name === 'word') currentCharIndex += event.charLength;
    };

    currentUtterance.onend = onEnd;
    synth.speak(currentUtterance);
  };

  const voices = synth.getVoices();
  if (voices.length === 0) {
    const handleVoicesChanged = () => {
      setVoiceAndSpeak();
      synth.removeEventListener('voiceschanged', handleVoicesChanged);
    };
    synth.addEventListener('voiceschanged', handleVoicesChanged);
    return;
  }

  setVoiceAndSpeak();
};
const speakStatusCue = (text, playbackRate = 1, onEnd) => {
  const synth = window.speechSynthesis;
  if (!synth || !text) return;
  if (isAudioMuted()) return;

  synth.cancel();

  const setVoiceAndSpeak = () => {
    const voices = synth.getVoices();
    const selectedVoice = selectFemaleVoice(voices);

    const cueUtterance = new SpeechSynthesisUtterance(text);

    if (selectedVoice) {
      cueUtterance.voice = selectedVoice;
      cueUtterance.lang = selectedVoice.lang;
    } else {
      cueUtterance.lang = 'en-GB';
    }

    // Status cues: slightly faster and clearer, warm and professional
    cueUtterance.rate = Math.min(1.05, Math.max(0.85, playbackRate * 0.9));
    cueUtterance.pitch = 1.06;
    cueUtterance.volume = 0.92;

    cueUtterance.onend = onEnd;
    synth.speak(cueUtterance);
  };

  const voices = synth.getVoices();
  if (voices.length === 0) {
    const handleVoicesChanged = () => {
      setVoiceAndSpeak();
      synth.removeEventListener('voiceschanged', handleVoicesChanged);
    };
    synth.addEventListener('voiceschanged', handleVoicesChanged);
    return;
  }

  setVoiceAndSpeak();
};

/* -------------------- STOP EVERYTHING -------------------- */
const stopAll = () => {
  SpeechRecognition.stopListening();
  window.speechSynthesis.cancel();
  stopWaitingPulseExternal();
  if (startFallbackTimer) {
    clearTimeout(startFallbackTimer);
    startFallbackTimer = null;
  }
  // Reset speech tracking variables to prevent resume issues
  currentText = '';
  currentCharIndex = 0;
  currentUtterance = null;
};

const pauseConversation = () => {
  SpeechRecognition.stopListening();
  window.speechSynthesis.cancel();
  stopWaitingPulseExternal();
  if (startFallbackTimer) {
    clearTimeout(startFallbackTimer);
    startFallbackTimer = null;
  }
  currentUtterance = null;
};

export default function AccessibleChatbot({
  fontScale = '1',
  setFontScale = () => {},
  highContrast = false,
  setHighContrast = () => {},
}) {
  const { theme, setTheme } = useTheme();
  const [history, setHistory] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [chatCompleted, setChatCompleted] = useState(false);
  const [persona, setPersona] = useState(null);
  const [courseList, setCoursesList] = useState([]);
  const [completionMessage, setCompletionMessage] = useState();
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const activeUser = localStorage.getItem('active_interview');
  // const isFirstTimeUser = Number(activeUser) === 2;
  // const activeUser = 2;

  const [welcomeSpoken, setWelcomeSpoken] = useState(false);
  const [sessionStarted, setSessionStarted] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  const isProcessingRef = useRef(false);
  const checkInTimerRef = useRef(null);
  const silencePromptTimerRef = useRef(null);
  const longSilenceTimerRef = useRef(null);
  const lastQuestionTextRef = useRef('');
  const hasAskedFirstQuestionRef = useRef(false);
  const lastPauseReminderAtRef = useRef(0);
  const hasStartedRef = useRef(false);
  const startRequestInFlightRef = useRef(false);
  const manualStopRef = useRef(false);
  const autoPlayHandledRef = useRef(false);
  const landingCuePlayedRef = useRef(false);
  const pausedStateRef = useRef(false);
  const [playbackRate, setPlaybackRate] = useState(
    Number(localStorage.getItem('playbackRate')) || 1
  );
  const [isMuted, setIsMuted] = useState(() => {
    try {
      return localStorage.getItem('rnibMuted') === 'true';
    } catch {
      return false;
    }
  });

  const waitingPulseRef = useRef({ ctx: null, timer: null });
  const waitingPulseDelayRef = useRef(null);
  const handleListenRef = useRef(() => {});
  const toggleMuteRef = useRef(() => {});
  const toggleThemeRef = useRef(() => {});
  const role = localStorage.getItem('role');
  const name = localStorage.getItem('name');
  const displayName = name && name.trim().length > 0 ? name.trim() : null;
  const WELCOME_MESSAGE = `Hello ${displayName} and welcome to our Travel and Technology Learning Resources. My name is Ruby and I am RNIBs digital learning advisor. These learning resources focus on enhancing awareness, knowledge and skills around technology, travel, and wellbeing. In order to create a tailored learning pathway for you, I’d like to start by asking a few questions to understand how I can best support you today with your tech, transport or travel learning requirements.`;
  const SILENCE_PROMPT =
    'Please take your time to respond, or if you prefer, just say repeat, rephrase or pause.';
  const HELP_PROMPT =
    'Please take your time answering each question, you can also say, repeat, rephrase, pause, or resume. ';
  const CHECK_IN_PROMPT =
    'Please take your time to respond, or if you prefer, just say repeat, rephrase or pause.';
  const NEXT_QUESTION_TRANSITION = 'Thank you. Tell me';
  // Auto-unmute on load in case mute persisted across refresh unexpectedly.
  useEffect(() => {
    const storedMute = localStorage.getItem('rnibMuted');
    if (storedMute === 'true') {
      localStorage.setItem('rnibMuted', 'false');
      setIsMuted(false);
      window.speechSynthesis.resume?.();
    }
  }, []);

  useEffect(() => {
    // Stop speech on page load / reload
    window.speechSynthesis.cancel();

    // Optional: clear tracking variables
    currentUtterance = null;
    currentText = '';
    currentCharIndex = 0;
    const message =
      Number(activeUser) === 3 ? `Thank you for continuing your journey with RNIB.` : '';
    setCompletionMessage(message);
  }, []);

  useEffect(() => {
    if (landingCuePlayedRef.current) return;
    if (welcomeSpoken) return;
    if (!(Number(activeUser) === 1 || Number(activeUser) === 2)) return;

    const message =
      'Welcome screen. Use Space or the microphone as your main conversation control.';
    setFeedbackMessage(message);

    if (
      localStorage.getItem('rnibAudioEnabled') === 'true' &&
      localStorage.getItem('rnibAutoplayAfterLogin') !== 'true' &&
      !isAudioMuted()
    ) {
      landingCuePlayedRef.current = true;
      const timer = setTimeout(() => {
        speakStatusCue(message, playbackRate);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [activeUser, playbackRate, welcomeSpoken]);

  const { transcript, listening, resetTranscript, browserSupportsSpeechRecognition } =
    useSpeechRecognition();

  useEffect(() => {
    pausedStateRef.current = isPaused;
  }, [isPaused]);

  // This is already correct in your code, no change needed:
  const announceFeedback = (message, options = {}) => {
    const { speakCue = true, historyText = null, onEnd } = options;
    setFeedbackMessage(message);
    if (historyText) addToHistory('bot', historyText);
    if (speakCue) {
      speakStatusCue(message, playbackRate, onEnd); // ✅ onEnd is passed
      return;
    }
    onEnd?.();
  };

  const stopWaitingPulse = () => {
    if (waitingPulseDelayRef.current) {
      clearTimeout(waitingPulseDelayRef.current);
      waitingPulseDelayRef.current = null;
    }

    if (waitingPulseRef.current.timer) {
      clearInterval(waitingPulseRef.current.timer);
      waitingPulseRef.current.timer = null;
    }

    if (waitingPulseRef.current.ctx) {
      waitingPulseRef.current.ctx.close();
      waitingPulseRef.current.ctx = null;
    }
  };

  const startWaitingPulse = () => {
    if (isAudioMuted()) return;
    if (waitingPulseRef.current.timer) return;
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;

    const ctx = new AudioContext();
    waitingPulseRef.current.ctx = ctx;

    const playTick = () => {
      if (!waitingPulseRef.current.ctx || isAudioMuted()) return;
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();

      oscillator.type = 'sine';
      oscillator.frequency.value = 520;
      gainNode.gain.setValueAtTime(0.0001, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.02, ctx.currentTime + 0.02);
      gainNode.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.14);

      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      oscillator.start();
      oscillator.stop(ctx.currentTime + 0.16);
    };

    playTick();
    waitingPulseRef.current.timer = setInterval(playTick, WAITING_PULSE_INTERVAL_MS);
  };

  const startWaitingPulseWithDelay = () => {
    stopWaitingPulse();
    if (isAudioMuted()) return;
    waitingPulseDelayRef.current = setTimeout(() => {
      startWaitingPulse();
    }, WAITING_PULSE_DELAY_MS);
  };

  useEffect(() => {
    stopWaitingPulseExternal = stopWaitingPulse;
    return () => {
      stopWaitingPulse();
      stopWaitingPulseExternal = () => {};
    };
  }, [isMuted]);

  /* -------------------- AUTO SCROLL -------------------- */
  const chatContainerRef = useRef(null);

  useEffect(() => {
    const container = chatContainerRef.current;
    if (!container) return;

    container.scrollTo({
      top: container.scrollHeight,
      behavior: 'smooth',
    });
  }, [history, chatCompleted]);

  const addToHistory = (sender, text) => {
    setHistory((prev) => [...prev, { sender, text }]);
  };

  const clearPauseTimers = () => {
    if (checkInTimerRef.current) {
      clearTimeout(checkInTimerRef.current);
      checkInTimerRef.current = null;
    }

    if (silencePromptTimerRef.current) {
      clearTimeout(silencePromptTimerRef.current);
      silencePromptTimerRef.current = null;
    }

    if (longSilenceTimerRef.current) {
      clearTimeout(longSilenceTimerRef.current);
      longSilenceTimerRef.current = null;
    }
  };

  const startListening = (interimResults = false) => {
    SpeechRecognition.startListening({
      continuous: true,
      interimResults,
      language: 'en-GB',
    });
  };

  const rememberQuestion = (questionText) => {
    if (!questionText) return;
    lastQuestionTextRef.current = questionText;
    setCurrentQuestion({ text: questionText });
  };

  const isHesitantResponse = (inputText) => {
    const normalized = inputText
      .toLowerCase()
      .replace(/[.,!?]/g, '')
      .trim();
    if (!normalized) return true;
    const hesitationPhrases = new Set([
      'um',
      'uh',
      'hmm',
      'erm',
      'maybe',
      'not sure',
      'i dont know',
      "i don't know",
      'can you repeat',
      'what',
    ]);

    if (hesitationPhrases.has(normalized)) return true;
    return normalized.split(/\s+/).length <= 1;
  };

  const isNegativeReply = (inputText) => {
    const normalized = inputText
      .toLowerCase()
      .replace(/[^\w\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    const negativePhrases = new Set([
      'no',
      'nope',
      'not now',
      'not today',
      'not interested',
      'dont want',
      'do not want',
      'dont want to continue',
      'do not want to continue',
      'stop',
      'stop now',
      'cancel',
      'end',
      'exit',
      'i can t listen',
      'can t listen',
      'cant listen',
      'cannot listen',
      'cant hear',
      'can t hear',
      'cannot hear',
      'cant do this',
      'can t do this',
      'cannot do this',
      'I can t hear you',
    ]);

    return negativePhrases.has(normalized);
  };

  const repeatQuestion = () => {
    const questionToRepeat = lastQuestionTextRef.current;
    if (!questionToRepeat) {
      addToHistory('bot', HELP_PROMPT);
      speak(HELP_PROMPT, playbackRate, () => startListening(false));
      return;
    }

    const message = `Sure, I will repeat the question. ${questionToRepeat}`;
    addToHistory('bot', message);
    speak(message, playbackRate, () => startListening(false));
  };

  const updateSpeed = (nextRate) => {
    const bounded = Math.min(1.1, Math.max(0.85, nextRate));
    setPlaybackRate(bounded);
    localStorage.setItem('playbackRate', String(bounded));
    return bounded;
  };

  const toggleMute = () => {
    const next = !isMuted;

    if (next) {
      stopWaitingPulse();
      setFeedbackMessage('Audio muted.');
      addToHistory('bot', 'Audio muted.');
      speakStatusCue('Audio muted.', playbackRate, () => {
        localStorage.setItem('rnibMuted', 'true');
        setIsMuted(true);
        window.speechSynthesis.cancel();
      });
      return;
    }

    localStorage.setItem('rnibMuted', 'false');
    setIsMuted(false);
    window.speechSynthesis.resume?.();
    startWaitingPulseWithDelay();
    setFeedbackMessage('Audio unmuted.');
    addToHistory('bot', 'Audio unmuted.');
    speakStatusCue('Audio unmuted.', playbackRate);
  };

  // AFTER:
  const toggleTheme = () => {
    const currentTheme = document.documentElement.getAttribute('data-theme') || theme || 'dark';
    const nextTheme = currentTheme === 'dark' ? 'light' : 'dark';
    document.documentElement.setAttribute('data-theme', nextTheme);
    document.body?.setAttribute('data-theme', nextTheme);
    localStorage.setItem('themeMode', 'manual');
    localStorage.setItem('theme', nextTheme);
    setTheme(nextTheme);

    // Snapshot what was playing before the cue cancels it
    const textToResume = lastQuestionTextRef.current || currentText || null;
    const wasListening = listening;

    announceFeedback(nextTheme === 'light' ? 'Light mode enabled.' : 'Dark mode enabled.', {
      speakCue: true,
      onEnd: () => {
        // After the cue finishes, restore what was happening
        if (chatCompleted || isPaused || manualStopRef.current) return;
        if (textToResume) {
          speak(textToResume, playbackRate, () => startListening(false));
        } else if (wasListening) {
          startListening(false);
        }
      },
    });
  };

  const fontSizeOptions = [
    { value: '1', label: '16px' },
    { value: '1.1', label: 'Large' },
    { value: '1.25', label: 'Larger' },
    { value: '1.4', label: 'Largest' },
  ];

  const handleHighContrastToggle = () => {
    setHighContrast((prev) => {
      const next = !prev;
      announceFeedback(
        next ? 'Blind and low vision support enabled.' : 'Blind and low vision support disabled.'
      );
      return next;
    });
  };

  const getCourseLink = (course, linkType = 'video') => {
    const keys =
      linkType === 'video'
        ? [
            'video_url',
            'videoLink',
            'video',
            'youtube_url',
            'youtube',
            'resource_url',
            'link',
            'url',
          ]
        : ['pdf_url', 'pdfLink', 'pdf', 'document_url', 'resource_url', 'link', 'url'];

    const isPdf = (url = '') => url.toLowerCase().includes('.pdf');

    const candidates = keys
      .map((key) => course?.[key])
      .filter((value) => typeof value === 'string' && value.startsWith('http'));

    if (!candidates.length) return null;

    if (linkType === 'pdf') {
      return candidates.find(isPdf) || null;
    }

    // video
    return candidates.find((url) => !isPdf(url)) || null;
  };

  const handleVoiceShortcut = (rawText) => {
    const normalized = rawText
      .toLowerCase()
      .replace(/[^\w\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    const isSlowerCommand =
      ['slower', 'slow down', 'a little slower'].includes(normalized) ||
      normalized.includes('slow the speed') ||
      normalized.includes('slow down the speed') ||
      normalized.includes('could you please slow down the speed');
    const isFasterCommand =
      ['faster', 'speed up', 'a little faster'].includes(normalized) ||
      normalized.includes('could you please speed up') ||
      normalized.includes('increase the speed') ||
      normalized.includes('go faster');

    if (isSlowerCommand) {
      const next = updateSpeed(playbackRate - 0.1);
      const msg = `Sure, I will speak a little slower. Speed is now ${next.toFixed(2)}.`;
      addToHistory('bot', msg);
      speak(msg, next, () => startListening(false));
      return true;
    }

    if (isFasterCommand) {
      const next = updateSpeed(playbackRate + 0.1);
      const msg = `Sure, I will speak a little faster. Speed is now ${next.toFixed(2)}.`;
      addToHistory('bot', msg);
      speak(msg, next, () => startListening(false));
      return true;
    }

    return false;
  };

  const resetSilencePromptTimer = () => {
    clearPauseTimers();

    if (!sessionStarted || chatCompleted || isPaused || window.speechSynthesis.speaking) return;
    if (!listening || transcript.trim()) return;

    // ⏱ Check-in after 10 seconds
    checkInTimerRef.current = setTimeout(() => {
      if (
        !sessionStarted ||
        chatCompleted ||
        window.speechSynthesis.speaking ||
        transcript.trim()
      ) {
        return;
      }

      SpeechRecognition.stopListening();

      addToHistory('bot', CHECK_IN_PROMPT);

      speak(CHECK_IN_PROMPT, playbackRate, () => {
        startListening(false);

        // optional long silence stop (example 2 minutes)
        longSilenceTimerRef.current = setTimeout(() => {
          if (!transcript.trim()) {
            stopAll();

            const exitMessage =
              'It seems you may be away right now. I will stop listening for now. You can press the microphone whenever you would like to continue.';

            addToHistory('bot', exitMessage);
            speak(exitMessage, playbackRate);
          }
        }, 120000);
      });
    }, 10000); // 10 seconds

    // ⏱ Silence prompt after 15 seconds
    silencePromptTimerRef.current = setTimeout(() => {
      if (
        !sessionStarted ||
        chatCompleted ||
        window.speechSynthesis.speaking ||
        transcript.trim()
      ) {
        return;
      }

      SpeechRecognition.stopListening();

      addToHistory('bot', SILENCE_PROMPT);

      speak(SILENCE_PROMPT, playbackRate, () => {
        startListening(false);
      });
    }, 15000); // 15 seconds
  };

  /* -------------------- START SESSION + FIRST QUESTION -------------------- */
  const startSessionAndQuestion = async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      console.error('[ERROR] No auth token found');
      return;
    }

    // Avoid duplicate /chat/start calls when the intro is interrupted and restarted quickly.
    if (startRequestInFlightRef.current) {
      return;
    }
    startRequestInFlightRef.current = true;

    try {
      const apiUrl = `${tmAIAssistantDefaultValue.tmAIChatbotApiUrl}chat/start`;

      const { data } = await axios.post(apiUrl, null, {
        headers: { Authorization: `Bearer ${token}` },
      });

      localStorage.setItem('session_id', data.chat_session_id);

      if (data?.question) {
        rememberQuestion(data.question.text);
        addToHistory('bot', data.question.text);

        speak(data.question.text, playbackRate, () => {
          startListening(true);
        });
        hasAskedFirstQuestionRef.current = true;
      }
    } catch (err) {
      console.error('[ERROR] /chat/start failed:', err.response?.status, err.message);

      if (shouldLogout(err)) {
        forceLogout();
        return;
      }

      // Add error message and allow retry
      const errorMessage =
        'I am having trouble starting our conversation. Please press L to try again.';
      addToHistory('bot', errorMessage);
      speak(errorMessage, playbackRate, () => {
        startListening(false);
      });

      // Reset state to allow retry
      hasStartedRef.current = false;
      setSessionStarted(false);
      setWelcomeSpoken(false);
    } finally {
      startRequestInFlightRef.current = false;
      if (startFallbackTimer) {
        clearTimeout(startFallbackTimer);
        startFallbackTimer = null;
      }
    }
  };
  const dynamicWelcomeMessage =
    Number(activeUser) === 1 || Number(activeUser) === 2 || Number(activeUser) === 3
      ? WELCOME_MESSAGE
      : "Welcome back to RNIB. I'm Ruby, your Learning Pathway Advisor. Thank you for returning. We are glad to continue your journey with you.";
  /* -------------------- HANDLE START -------------------- */
  const handleStart = async () => {
    if (hasStartedRef.current || sessionStarted) return;
    hasStartedRef.current = true;
    setSessionStarted(true);

    if (!welcomeSpoken) {
      setWelcomeSpoken(true);
      addToHistory('bot', dynamicWelcomeMessage);

      speak(dynamicWelcomeMessage, playbackRate, async () => {
        addToHistory('bot', HELP_PROMPT);
        speak(HELP_PROMPT, playbackRate, async () => {
          await startSessionAndQuestion();
        });
      });

      // Start listening immediately even during welcome message
      setTimeout(() => {
        if (pausedStateRef.current || manualStopRef.current) return;
        if (!listening && !window.speechSynthesis.speaking) {
          startListening(false);
        }
      }, 1000);

      // Fallback: If callbacks fail, call start API after welcome + help prompt should have finished
      // Welcome (~20-30s) + Help (~10-15s) + buffer = ~50s
      setTimeout(() => {
        if (pausedStateRef.current || manualStopRef.current) return;
        if (!hasAskedFirstQuestionRef.current && !startRequestInFlightRef.current) {
          console.log(
            '[DEBUG] Fallback: Calling startSessionAndQuestion (callbacks may have failed)'
          );
          startSessionAndQuestion();
        }
      }, 50000);
    } else {
      await startSessionAndQuestion();
    }
  };

  useEffect(() => {
    if (autoPlayHandledRef.current) return;
    if (localStorage.getItem('rnibAutoplayAfterLogin') !== 'true') return;

    autoPlayHandledRef.current = true;
    localStorage.setItem('rnibAutoplayAfterLogin', 'false');
    localStorage.setItem('rnibAudioEnabled', 'true');

    const autoStartTimer = setTimeout(() => {
      handleStart();
    }, 400);

    return () => clearTimeout(autoStartTimer);
  }, []);

  /* -------------------- ANSWER QUESTION -------------------- */
  const handleUserQuestion = async (answer) => {
    if (isPaused || pausedStateRef.current) return;
    addToHistory('user', answer);

    if (isNegativeReply(answer)) {
      repeatQuestion();
      return;
    }

    const thinkingMessages = [
      'Please give me a moment.',
      'One moment while I prepare the next question.',
      'Just a moment.',
      'Please wait a moment.',
    ];
    const thinkingMessage = thinkingMessages[Math.floor(Math.random() * thinkingMessages.length)];
    addToHistory('bot', thinkingMessage);
    if (!isPaused && !pausedStateRef.current) {
      speak(thinkingMessage, playbackRate);
      startWaitingPulseWithDelay();
    }

    const token = localStorage.getItem('authToken');
    const sessionId = localStorage.getItem('session_id');

    try {
      const { data } = await axios.post(
        `${tmAIAssistantDefaultValue.tmAIChatbotApiUrl}chat/answer`,
        null,
        {
          params: { answer, chat_session_id: sessionId },
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (data?.status === 'chat_closed') {
        setChatCompleted(true);
        setPersona(data.final_persona || null);
        setCoursesList(data.courses);

        localStorage.setItem('active_interview', data.active_interview);

        const courseSpeech = (data?.courses || [])
          .map(
            (course, idx) =>
              `Module ${idx + 1}: ${course.title}${course?.summary?.text ? `. ${course.summary.text}` : ''}`
          )
          .join(' ');

        if (data?.courses?.length) {
          const courseListForHistory = data.courses
            .map(
              (course, idx) =>
                `${idx + 1}. ${course.title}${course?.summary?.text ? ` — ${course.summary.text}` : ''}`
            )
            .join('\n');
          addToHistory('bot', `Here are your recommended courses:\n${courseListForHistory}`);
        }

        const message =
          data?.reason === 'INAPPROPRIATE_ANSWERS_LIMIT_REACHED'
            ? 'I’m sorry, I wasn’t able to understand the response clearly. We’ll end the conversation for now. Please log in again whenever you’re ready to continue.'
            : data.active_interview === 2
              ? 'Thank you. Based on your responses and learning priorities, here are our recommendations for your personalised Learning Pathway'
              : data.active_interview === 3
                ? 'Thank you for continuing your journey with RNIB. We truly appreciate your time today.'
                : 'Thank you for your responses.';
        setCompletionMessage(message);
        addToHistory('bot', message);

        stopAll();
        if (Number(activeUser) === 1 || Number(activeUser) === 2 || Number(activeUser) === 3) {
          speak(message, playbackRate, () => {
            if (courseSpeech) {
              speak(courseSpeech, playbackRate);
            }
          });
        }
        return;
      }

      if (data?.next_question) {
        rememberQuestion(data.next_question.text);
        const nextQuestionMessage = hasAskedFirstQuestionRef.current
          ? ` ${data.next_question.text}`
          : data.next_question.text;
        addToHistory('bot', nextQuestionMessage);
        hasAskedFirstQuestionRef.current = true;

        speak(nextQuestionMessage, playbackRate, () => {
          startListening(false);
        });
      }
    } catch (err) {
      console.error(err);
      stopWaitingPulse();

      // Handle different error scenarios
      if (shouldLogout(err)) {
        forceLogout();
        return;
      }

      // Add error message to history and speak it
      const errorMessage =
        'I am sorry, something went wrong. Please try again or press L to restart.';
      addToHistory('bot', errorMessage);
      speak(errorMessage, playbackRate, () => {
        startListening(false);
      });

      // Reset session state to allow recovery
      if (!currentQuestion) {
        hasStartedRef.current = false;
        setSessionStarted(false);
        setWelcomeSpoken(false);
        setCurrentQuestion(null);
      }
    } finally {
      stopWaitingPulse();
    }
  };

  /* -------------------- PROCESS SPEECH -------------------- */
  useEffect(() => {
    if (!transcript.trim()) return;

    clearPauseTimers();

    const silenceTimer = setTimeout(() => {
      if (!isProcessingRef.current) {
        isProcessingRef.current = true;

        const finalTranscript = transcript.trim();
        const normalizedTranscript = finalTranscript.toLowerCase();
        const resumeCommand =
          normalizedTranscript.includes('resume') || normalizedTranscript.includes('continue');
        const echoFromAssistant =
          normalizedTranscript.includes('you can answer in your own words') ||
          normalizedTranscript.includes('you may also say repeat') ||
          normalizedTranscript.includes('thank you for waiting') ||
          normalizedTranscript.includes('are you still there');
        const pauseCommand =
          normalizedTranscript === 'pause' ||
          normalizedTranscript === 'pause please' ||
          normalizedTranscript === 'stop';

        if (pauseCommand) {
          SpeechRecognition.stopListening();
          resetTranscript();
          setIsPaused(true);
          pauseConversation();
          setFeedbackMessage('Conversation paused.');
          addToHistory('bot', 'Conversation paused.');
          speakStatusCue('Conversation paused.', playbackRate);
          isProcessingRef.current = false;
          return;
        }

        if (echoFromAssistant) {
          resetTranscript();
          isProcessingRef.current = false;
          return;
        }

        if (isPaused && !resumeCommand) {
          resetTranscript();
          isProcessingRef.current = false;
          return;
        }

        // Keep listening active for fumbles/hesitation and avoid long repeated prompts.
        // if (isHesitantResponse(finalTranscript)) {
        //   const now = Date.now();
        //   if (now - lastFumbleNudgeAtRef.current > FUMBLE_NUDGE_COOLDOWN_MS) {
        //     const nudge = 'Thank you. I am still listening. Please take your time and continue.';
        //     addToHistory('bot', nudge);
        //     speak(nudge, playbackRate);
        //     lastFumbleNudgeAtRef.current = now;
        //   }
        //   resetTranscript();
        //   isProcessingRef.current = false;
        //   return;
        // }

        // Handle shortcuts and API calls after pausing recognition.
        SpeechRecognition.stopListening();
        resetTranscript();

        if (handleVoiceShortcut(finalTranscript)) {
          isProcessingRef.current = false;
          return;
        }

        handleUserQuestion(finalTranscript).finally(() => {
          isProcessingRef.current = false;
        });
      }
    }, USER_RESPONSE_DEBOUNCE_MS);

    return () => clearTimeout(silenceTimer);
  }, [transcript, isPaused]);

  useEffect(() => {
    resetSilencePromptTimer();
    return () => clearPauseTimers();
  }, [listening, transcript, sessionStarted, chatCompleted, playbackRate]);

  useEffect(() => {
    const handleShortcut = (e) => {
      const key = typeof e.key === 'string' ? e.key.toLowerCase() : '';

      if (e.code === 'Space' || e.key === ' ') {
        e.preventDefault();
        handleListenRef.current();
      }

      if (key === 'l') {
        handleListenRef.current();
      }

      if (e.key === 'Escape') {
        const interruptedDuringIntro = welcomeSpoken && !hasAskedFirstQuestionRef.current;
        stopAll();
        setIsPaused(false);
        manualStopRef.current = true;
        announceFeedback('Conversation stopped.');

        // If the user stops Ruby while the welcome message is still in progress,
        // ensure we still hit the start API shortly after.
        if (interruptedDuringIntro) {
          setTimeout(() => {
            if (!hasAskedFirstQuestionRef.current && !startRequestInFlightRef.current) {
              startSessionAndQuestion();
            }
          }, 1000);
        }
      }

      if (key === 'm') {
        toggleMuteRef.current();
      }

      if (key === 'i') {
        e.preventDefault();
        toggleThemeRef.current();
      }
    };

    window.addEventListener('keydown', handleShortcut);
    return () => window.removeEventListener('keydown', handleShortcut);
  }, [chatCompleted, welcomeSpoken, announceFeedback, startSessionAndQuestion]);

  /* -------------------- TAP / MIC HANDLER -------------------- */
  const handleListen = () => {
    if (chatCompleted) return;

    const scheduleStartFallback = () => {
      if (startFallbackTimer) return;
      startFallbackTimer = setTimeout(() => {
        if (!hasAskedFirstQuestionRef.current && !window.speechSynthesis.speaking) {
          console.log('[DEBUG] Calling startSessionAndQuestion from scheduleStartFallback');
          const msg = 'Starting now, thanks for waiting.';
          addToHistory('bot', msg);
          speak(msg, playbackRate, () => startListening(false));
        }
      }, 7000);
    };

    // If user previously hit Esc, treat next L as a resume/start intent.
    if (manualStopRef.current) {
      console.log('[DEBUG] Manual stop was triggered, resuming');
      manualStopRef.current = false;
      setIsPaused(false);
      setFeedbackMessage('Conversation resumed.');
      addToHistory('bot', 'Conversation resumed.');

      const resumeText = lastQuestionTextRef.current || currentText;

      // If we already have a question, repeat it then listen.
      if (resumeText) {
        speakStatusCue('Conversation resumed.', playbackRate, () => {
          speak(resumeText, playbackRate, () => startListening(false));
        });
        return;
      }

      // Otherwise ensure we call start and begin listening.
      if (!startRequestInFlightRef.current) {
        console.log('[DEBUG] Calling startSessionAndQuestion from manualStopRef');
        startSessionAndQuestion();
      }
      speakStatusCue('Conversation resumed.', playbackRate, () => {
        startListening(false);
        scheduleStartFallback();
      });
      return;
    }

    // ⏸ Pause
    if (isPaused) {
      console.log('[DEBUG] Resuming from paused state');
      setIsPaused(false);
      setFeedbackMessage('Conversation resumed.');
      addToHistory('bot', 'Conversation resumed.');

      const resumeText =
        lastQuestionTextRef.current || currentQuestion?.text || currentText || null;

      if (resumeText) {
        speakStatusCue('Conversation resumed.', playbackRate, () => {
          speak(resumeText, playbackRate, () => {
            startListening(false);
          });
        });
        return;
      }

      speakStatusCue('Conversation resumed.', playbackRate, () => {
        startListening(false);
      });
      return;
    }

    if (listening || window.speechSynthesis.speaking) {
      console.log('[DEBUG] Pausing audio/listening');
      setIsPaused(true);
      clearPauseTimers();
      pauseConversation();
      setFeedbackMessage('Conversation paused.');
      addToHistory('bot', 'Conversation paused.');
      speakStatusCue('Conversation paused.', playbackRate);
      return;
    }

    resetTranscript();

    // ▶ Resume speech
    if (currentText && currentCharIndex > 0) {
      console.log('[DEBUG] Resuming speech');
      setFeedbackMessage('Conversation resumed.');
      addToHistory('bot', 'Conversation resumed.');
      speakStatusCue('Conversation resumed.', playbackRate, () => {
        speak(lastQuestionTextRef.current || currentText, playbackRate, () => {
          startListening(false);
        });
      });
      if (!sessionStarted) {
        console.log('[DEBUG] Session not started, calling handleStart from resume');
        handleStart();
        return;
      }
      return;
    }

    // ▶ If speech was stopped (Esc) and we have a current question, replay it then listen.
    if (sessionStarted && hasAskedFirstQuestionRef.current && lastQuestionTextRef.current) {
      console.log('[DEBUG] Replaying question');
      setFeedbackMessage('Conversation resumed.');
      addToHistory('bot', 'Conversation resumed.');
      speakStatusCue('Conversation resumed.', playbackRate, () => {
        speak(lastQuestionTextRef.current, playbackRate, () => {
          startListening(false);
        });
      });
      return;
    }

    // ▶ Start flow
    if (!sessionStarted) {
      console.log('[DEBUG] Session not started, calling handleStart');
      handleStart();
      scheduleStartFallback();
      return;
    }

    // If no current question or in welcome phase, start listening immediately
    if (!currentQuestion || !hasAskedFirstQuestionRef.current) {
      // If welcome was interrupted (welcomeSpoken is true but sessionStarted is false), just start listening
      if (welcomeSpoken && !hasAskedFirstQuestionRef.current) {
        startListening(false);
        // Ensure the first question is fetched even if the intro was cut short.
        if (!startRequestInFlightRef.current) {
          setTimeout(() => {
            if (!hasAskedFirstQuestionRef.current) {
              console.log('[DEBUG] Calling startSessionAndQuestion from welcome interrupt');
              startSessionAndQuestion();
            }
          }, 1000);
        }
        return;
      }

      // If no current question, immediately start listening
      if (!currentQuestion) {
        console.log('[DEBUG] No current question, starting listening');
        setFeedbackMessage('Conversation resumed.');
        addToHistory('bot', 'Conversation resumed.');
        speakStatusCue('Conversation resumed.', playbackRate, () => {
          startListening(false);
          scheduleStartFallback();
        });
        return;
      }

      // Reset session state to allow restart
      console.log('[DEBUG] Resetting session state');
      hasStartedRef.current = false;
      setSessionStarted(false);
      setWelcomeSpoken(false);
      setCurrentQuestion(null);
      handleStart();
      return;
    }

    setFeedbackMessage('Conversation resumed.');
    addToHistory('bot', 'Conversation resumed.');
    speakStatusCue('Conversation resumed.', playbackRate, () => {
      startListening(false);
    });
  };

  useEffect(() => {
    handleListenRef.current = handleListen;
  }, [handleListen]);

  useEffect(() => {
    toggleMuteRef.current = toggleMute;
  }, [toggleMute]);

  useEffect(() => {
    toggleThemeRef.current = toggleTheme;
  }, [toggleTheme]);

  if (!browserSupportsSpeechRecognition) {
    return <p>Please use Chrome for voice support.</p>;
  }

  const accessibilitySidebar = (
    <section aria-label="Accessibility preferences">
      <h2 className="text-[24px] font-bold text-text-light">Accessibility Preferences</h2>
      <p className="mt-2 text-base text-text-muted">
        Adjust visual preferences alongside the shortcut keys.
      </p>

      <div className="mt-4 space-y-5">
        <div>
          <label className="text-base font-semibold text-text-light">Font Size</label>
          <div className="mt-3 flex gap-3 flex-wrap">
            {fontSizeOptions.map((option) => {
              const isActive = fontScale === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    setFontScale(option.value);
                    // ✅ Speak confirmation, then resume what was playing
                    const textToResume = lastQuestionTextRef.current || currentText || null;
                    const wasListening = listening;
                    speakStatusCue(`Font size set to ${option.label}.`, playbackRate, () => {
                      if (chatCompleted || isPaused || manualStopRef.current) return;
                      if (textToResume) {
                        speak(textToResume, playbackRate, () => startListening(false));
                      } else if (wasListening) {
                        startListening(false);
                      }
                    });
                  }}
                  className={`px-2 py-2 rounded-lg border font-semibold transition focus:outline-none focus:ring-2 focus:ring-accent ${
                    isActive
                      ? 'bg-accent text-[color:var(--accent-foreground)] border-accent shadow-md'
                      : 'bg-bg text-text-light border-glass-border hover:bg-muted'
                  }`}
                  aria-pressed={isActive}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label className="text-base font-semibold text-text-light">
            Blind and Low-Vision Support
          </label>
          <div className="mt-3 flex flex-col items-center justify-between gap-4 rounded-lg border border-glass-border bg-bg px-2 py-2">
            <p className="text-left text-base text-text-light">
              Uses stronger light and dark contrast across controls, panels, and text, with spoken
              confirmation when changed.
            </p>
            <button
              type="button"
              onClick={handleHighContrastToggle}
              className={`min-w-[7rem] rounded-lg border px-2 py-2 font-semibold transition focus:outline-none focus:ring-2 focus:ring-accent mb-4 ${
                highContrast
                  ? 'bg-accent text-[color:var(--accent-foreground)] border-accent'
                  : 'bg-panel text-text-light border-glass-border hover:bg-muted'
              }`}
              aria-pressed={highContrast}
            >
              {highContrast ? 'On' : 'Off'}
            </button>
          </div>
        </div>
      </div>
    </section>
  );

  /* -------------------- UI -------------------- */

  return (
    <>
      <Navbar sidebarExtra={accessibilitySidebar} />

      <main className="mx-auto flex min-h-[calc(100vh-90px)] w-full max-w-screen-2xl flex-col items-center bg-panel px-3 pb-12 text-left text-text-light sm:px-6 xl:pr-[37rem] 2xl:pr-[41rem]">
        {/* Welcome */}
        {!welcomeSpoken && (Number(activeUser) === 1 || Number(activeUser) === 2) && (
          <section className="mb-8 w-full max-w-5xl rounded-lg border border-glass-border p-5 text-left shadow-sm sm:p-8 mt-5">
            <h1 className="text-[24px] font-bold mb-4 text-text-light">Welcome</h1>
            <p>
              Thank you for joining us. I will guide you through each step with simple voice
              prompts. Press Space or the microphone to start, pause the voice session. On touch
              devices, use the microphone button.
            </p>
            {/* <p
              role="status"
              aria-live="polite"
              className="mt-5 max-w-4xl text-base font-semibold text-text-light break-words"
            >
              {feedbackMessage}
            </p> */}

            {/* <div className="mt-4 text-left">
              <h2 className="text-lg font-semibold mb-2">Keyboard shortcuts</h2>
              <ul className="space-y-1 text-base">
                <li><strong>L</strong> Start or stop listening</li>
                <li><strong>Esc</strong> Stop speech and listening</li>
                <li><strong>I</strong> Invert colors (light or dark)</li>
                <li><strong>M</strong> Mute or unmute audio</li>
              </ul>
            </div> */}
          </section>
        )}
        {!chatCompleted && (Number(activeUser) === 1 || Number(activeUser) === 2) && (
          <>
            {/* Mic Button */}
            <button
              onClick={handleListen}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleListen();
                }
              }}
              aria-label={
                listening || window.speechSynthesis.speaking
                  ? 'Pause or stop the voice session'
                  : 'Start or resume the voice session'
              }
              aria-pressed={listening}
              aria-describedby="voice-status"
              className="h-32 aspect-square rounded-full border-2 border-glass-border
  flex items-center justify-center bg-bg shadow-lg transition hover:bg-muted
  focus:outline-none focus:ring-4 focus:ring-accent mt-5"
            >
              {listening ? (
                <HiStop className="text-2xl text-red-500" />
              ) : (
                <HiMicrophone className="text-2xl text-text-light" />
              )}
            </button>

            <p
              id="voice-status"
              role="status"
              aria-live="polite"
              className="mt-6 max-w-4xl px-2 text-center text-base font-semibold text-text-light break-words"
            >
              {listening
                ? 'Listening...'
                : window.speechSynthesis.speaking
                  ? 'Ruby speaking. Press Space or the microphone to pause.'
                  : feedbackMessage || 'Press Space or the microphone to start or resume.'}
            </p>
            <div className="mt-8 w-full max-w-3xl rounded-xl border border-glass-border bg-panel px-4 py-5 sm:px-6 xl:hidden">
              <h2 className="text-[24px] font-bold text-text-light">Accessibility Preferences</h2>

              <div className="mt-5 grid gap-6 lg:grid-cols-2">
                <div>
                  <label className="text-sm font-semibold text-text-light">Font Size</label>
                  <div className="mt-3 flex flex-wrap gap-3">
                    {fontSizeOptions.map((option) => {
                      const isActive = fontScale === option.value;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => setFontScale(option.value)}
                          className={`px-2 py-2 rounded-lg border font-semibold transition focus:outline-none focus:ring-2 focus:ring-accent ${
                            isActive
                              ? 'bg-accent text-[color:var(--accent-foreground)] border-accent shadow-md'
                              : 'bg-bg text-text-light border-glass-border hover:bg-muted'
                          }`}
                          aria-pressed={isActive}
                        >
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* <div>
                  <label className="text-sm font-semibold text-text-light">Blind and Low-Vision Support</label>
                  <div className="mt-3 flex flex-col items-center justify-between gap-4 rounded-lg border border-glass-border bg-bg px-2 py-2">
                    <p className="text-left text-sm text-text-light">
                      Uses stronger light and dark contrast across controls, panels, and text, with spoken confirmation when changed.
                    </p>
                    <button
                      type="button"
                      onClick={handleHighContrastToggle}
                      role="switch"
                      aria-checked={highContrast}
                      aria-label={`Blind and Low-Vision Support ${highContrast ? 'on' : 'off'}`}
                      className={`relative inline-flex h-14 w-[10rem] items-center rounded-full border px-2 transition focus:outline-none focus:ring-2 focus:ring-accent ${
                        highContrast
                          ? 'border-accent bg-accent/20'
                          : 'border-glass-border bg-panel hover:bg-muted'
                      }`}
                    >
                      <span
                        className={`absolute left-2 inline-flex h-10 w-[4.25rem] items-center justify-center rounded-full font-semibold transition ${
                          highContrast
                            ? 'translate-x-[4.85rem] bg-accent text-[color:var(--accent-foreground)]'
                            : 'translate-x-0 bg-bg text-text-light'
                        }`}
                      >
                        {highContrast ? 'On' : 'Off'}
                      </span>
                    </button>
                  </div>
                </div> */}
              </div>
            </div>

            {/* Playback Speed Control */}
            <div className="mt-6 flex w-full max-w-xl flex-col items-center px-1 sm:px-0">
              <label className="text-base font-semibold mb-3 text-text-light">Playback Speed</label>

              <div className="flex gap-3 flex-wrap justify-center">
                {[0.9, 1, 1.1, 1.2].map((rate) => {
                  const isActive = playbackRate === rate;
                  return (
                    <button
                      key={rate}
                      onClick={() => {
                        setPlaybackRate(rate);
                        localStorage.setItem('playbackRate', rate);

                        // Restart speech with new rate if speaking
                        if (window.speechSynthesis.speaking) {
                          window.speechSynthesis.cancel();
                          speak(currentText, rate);
                        }
                      }}
                      className={`px-2 py-2 rounded-lg border text-base font-semibold transition focus:outline-none focus:ring-2 focus:ring-accent ${
                        isActive
                          ? 'bg-accent text-[color:var(--accent-foreground)] border-accent shadow-md'
                          : 'bg-panel text-text-light border-glass-border hover:bg-muted'
                      }`}
                      aria-pressed={isActive}
                    >
                      {rate}x
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Chat History */}
            {history.length > 0 && (
              <section
                ref={chatContainerRef}
                role="log"
                aria-live="polite"
                aria-relevant="additions text"
                aria-atomic="false"
                className="mt-6 w-full max-w-4xl px-1 text-left sm:px-0 max-h-[calc(100vh-420px)] overflow-y-auto scroll-smooth"
              >
                <ol className="space-y-4">
                  {history
                    .filter(
                      (h) =>
                        h.text !== '✅ Chat Completed.' && !h.text.startsWith('Persona identified:')
                    )
                    .map((h, i) => {
                      const isUser = h.sender === 'user';
                      const isError = h.type === 'error';

                      return (
                        <li key={i} className="w-full">
                          <div
                            className={`flex w-full items-start gap-3 pb-5 ${
                              isUser ? 'justify-end' : 'justify-start'
                            }`}
                          >
                            <span
                              aria-hidden="true"
                              className={`h-12 w-12 min-w-[48px] min-h-[48px] rounded-full border border-glass-border bg-muted flex items-center justify-center shrink-0 ${
                                isUser ? 'order-2' : 'order-1'
                              }`}
                            >
                              {isUser ? <UserRound size={26} /> : <Bot size={26} />}
                            </span>

                            <div
                              className={`max-w-[min(92%,34rem)] px-4 py-3 rounded-2xl border text-base leading-relaxed sm:px-5 ${
                                isUser
                                  ? 'order-1 bg-accent text-[color:var(--accent-foreground)] border-accent text-left'
                                  : 'order-2 bg-panel text-text-light border-glass-border text-left'
                              } ${isError ? 'border-red-500 text-red-300 bg-red-950/30' : ''}`}
                            >
                              {/* Screen reader label */}
                              <span className="sr-only">
                                {isUser ? 'Message from you: ' : 'Message from assistant: '}
                              </span>

                              {/* Visible speaker label */}
                              <span className="block mb-2 text-base font-bold">
                                {isUser ? 'You' : 'Ruby'}
                              </span>

                              {/* Message text */}
                              <p className="text-left leading-relaxed break-words text-base">
                                {h.text}
                              </p>
                            </div>
                          </div>
                        </li>
                      );
                    })}
                </ol>
              </section>
            )}
          </>
        )}

        {/* Completion Message */}
        {chatCompleted ? (
          <section className="mt-6 max-w-2xl w-full border border-glass-border rounded-lg p-6 text-left shadow-sm z-50">
            <h2 className="text-xl font-bold mb-3">Chat Completed</h2>
            <p>{completionMessage}</p>
          </section>
        ) : Number(activeUser) === 3 ? (
          <section className="mt-6 max-w-2xl w-full border border-glass-border rounded-lg p-6 text-left shadow-sm z-50">
            <h2 className="text-xl font-bold mb-3">Chat Completed</h2>
            <p>{completionMessage}</p>
          </section>
        ) : null}

        {/* Courses */}
        {chatCompleted && Number(activeUser) === 2 && courseList.length > 0 && (
          <section className="z-50 mt-4 w-full max-w-5xl overflow-y-auto px-2 sm:px-0 max-h-[60vh]">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-center">
              Personalised learning Pathways
            </h2>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {courseList.map((course, index) => (
                <div
                  key={index}
                  className="group border border-glass-border rounded-2xl p-5 bg-panel shadow-md hover:shadow-xl transition-all duration-300"
                >
                  {/* Course Number Badge */}
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm font-semibold text-text-muted">
                      Module {index + 1}
                    </span>

                    {/* <span className="px-3 py-1 text-xs font-medium bg-bg text-text-light rounded-full">
                        Recommended
                      </span> */}
                  </div>

                  {/* Title */}
                  <h3 className="text-xl font-bold group-hover:text-text-light">{course.title}</h3>

                  {course?.summary?.text && (
                    <p className="text-base text-text-muted leading-relaxed mt-3">
                      {course.summary.text}
                    </p>
                  )}

                  {/* {course.page && (
                    <p className="text-text-muted text-sm leading-relaxed mb-4">
                      Page {course.page}
                    </p>
                  )} */}

                  <div className="mt-3 flex items-center gap-4 flex-wrap">
                    {getCourseLink(course, 'video') && (
                      <a
                        href={getCourseLink(course, 'video')}
                        target="_blank"
                        rel="noreferrer"
                        className="underline text-teal-accent text-sm font-semibold"
                      >
                        Watch Video
                      </a>
                    )}
                    {getCourseLink(course, 'pdf') && (
                      <a
                        href={getCourseLink(course, 'pdf')}
                        target="_blank"
                        rel="noreferrer noopener"
                        download
                        className="underline text-yellow-accent text-sm font-semibold"
                      >
                        Download PDF
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>
    </>
  );
}
